/**
 * @module Cart/_components/PopUpCartFooter
 * @description A component that renders the footer section of the popup cart, displaying subtotal,
 * view cart and checkout buttons, and minimum order quantity message.
 */
"use client";
import { Button } from "@/components/ui/button";
import { Caption, Paragraph, Title } from "@/components/ui/typography";
import { ShoppingBag } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { getCookieValue } from "@/app/utils/utility";
import { useSelector } from "react-redux";

/**
 * PopUpCartFooter component that displays the footer of the popup cart.
 * Contains subtotal price, view cart link, checkout button and minimum order message.
 * The component adapts its layout and content based on user authentication status and viewport size.
 *
 * @function PopUpCartFooter
 * @param {Object} props - Component props
 * @param {string} [props.subTotalPrice="$00.00"] - The formatted subtotal price of items in cart
 * @param {Function} props.handleCheckoutClick - Callback function triggered when checkout button is clicked
 * @param {Function} props.handleToCartClick - Callback function triggered when view cart link is clicked
 * @returns {JSX.Element} The rendered popup cart footer component
 *
 * @example
 * // Basic usage
 * <PopUpCartFooter
 *   subTotalPrice="$99.99"
 *   handleCheckoutClick={() => handleCheckout()}
 *   handleToCartClick={() => navigateToCart()}
 * />
 *
 * @example
 * // With default subtotal
 * <PopUpCartFooter
 *   handleCheckoutClick={() => console.log('Checkout clicked')}
 *   handleToCartClick={() => console.log('View cart clicked')}
 * />
 *
 * @remarks
 * - The component uses Redux for managing authentication state
 * - Checkout button text changes based on user login status
 * - Cart link is only active when user is logged in
 * - Minimum order message is only visible on mobile and tablet views
 * - Component is responsive with different layouts for mobile, tablet and desktop
 *
 * @see {@link Cart/_components/PopUpCart} - Parent component
 * @see {@link https://redux.js.org/} - Redux documentation for state management
 */
export default function PopUpCartFooter({
  handleCheckoutClick,
  handleToCartClick,
  addressListData = [],
}) {
  const [accessToken, setAccessToken] = useState(getCookieValue("accessToken"));
  const subTotal = useSelector((state) => state?.cart?.cartDetails?.subTotal);
  useEffect(() => {
    const handleTokenChange = () => {
      setAccessToken(getCookieValue("accessToken"));
    };
    window.addEventListener("storage", handleTokenChange);
    return () => {
      window.removeEventListener("storage", handleTokenChange);
    };
  }, []);
  return (
    <div className="fixed bottom-0 flex justify-center items-center w-full md:w-[766px] lg:w-[574px] p-4 z-10 border-t border-gray-200 bg-white">
      <div className="w-full md:w-[720px] ">
        <div className="flex justify-between items-center mt-4 lg:mt-0">
          <Paragraph>Subtotal</Paragraph>
          <Title size="md">{subTotal}</Title>
        </div>
        <div className="flex flex-col md:flex-row justify-between gap-2 lg:gap-4 mt-4 items-center ">
          <Link
            href={accessToken ? "/cart" : ""}
            onClick={handleToCartClick}
            className="w-full md:w-1/2 mr-1 outline outline-1 outline-midnightIndigo-200 hover:outline-midnightIndigo-400 text-center h-10 flex justify-center items-center rounded text-[14px] lg:text-[16px]"
          >
            View cart
          </Link>

          <Button
            className="!w-full md:!w-1/2 md:m-0 text-[14px] lg:text-[16px]"
            onClick={handleCheckoutClick}
          >
            {accessToken ? "Proceed To Checkout" : "Sign up — Early access"}
          </Button>
        </div>

        {addressListData?.length > 0 ? (
          <div className="flex gap-1 mt-4 mb-4  bg-system-azure p-[10px] mb-15">
            <ShoppingBag className="h-[20px] w-[20px]" />
            <Caption className="ml-2 text-midnightIndigo-400">
              Minimum order quantity is 10 items for international orders, and 5
              items within the US.
            </Caption>
          </div>
        ) : (
          ""
        )}
      </div>
    </div>
  );
}
